import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AppLayout } from "@/components/AppLayout";

export default function Careers() {
  const openings = [
    {
      title: "Full Stack Developer",
      type: "Remote",
      description: "Help build and scale our resume tailoring engine and user experience tools."
    },
    {
      title: "Customer Success Specialist", 
      type: "Remote",
      description: "Guide users through onboarding, troubleshoot issues, and gather feedback to improve the platform."
    },
    {
      title: "Growth Marketer",
      type: "Remote", 
      description: "Own the customer acquisition funnel across paid, organic, and affiliate channels."
    },
    {
      title: "Content Writer – Career & Resume Strategy",
      type: "Freelance/Contract",
      description: "Create blog posts, help center articles, and marketing content for job seekers."
    }
  ];

  const benefits = [
    "100% remote team",
    "Flexible hours", 
    "Impact-driven work",
    "Room to grow and lead",
    "Real user feedback from people we're helping every day"
  ];

  return (
    <AppLayout currentPage="careers">
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-8 pb-8 max-w-4xl">
          <h1 className="text-4xl font-bold text-foreground mb-4">Join the TailorWiz Team</h1>
          <p className="text-xl text-muted-foreground">
            We're building the future of career tools. Come be part of it.
          </p>
        </div>
        
        <main className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="mb-12">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl mb-6">
              At TailorWiz, we believe in helping people reach their goals—and that starts with how we build our own team.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
              We're a remote-first, mission-driven company focused on making the job search smarter, faster, and more human. If you're passionate about innovation, AI, and making a real impact on people's lives, we'd love to meet you.
            </p>
          </div>

          <section className="mb-16">
            <div className="flex items-center gap-2 mb-8">
              <span className="text-2xl">🌍</span>
              <h2 className="text-3xl font-bold text-foreground">Current Openings</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {openings.map((job, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl mb-2">{job.title}</CardTitle>
                        <Badge variant="secondary" className="bg-primary/10 text-primary">
                          {job.type}
                        </Badge>
                      </div>
                      <span className="text-2xl font-bold text-primary">{index + 1}.</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed mb-4">
                      {job.description}
                    </CardDescription>
                    <Button variant="outline" className="w-full">
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <Card className="mt-8 bg-muted/50">
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">
                  Don't see the right fit? We're always open to talented people—send us a pitch at{" "}
                  <a href="mailto:hiring@tailorwiz.com" className="text-primary hover:underline font-medium">
                    hiring@tailorwiz.com
                  </a>
                </p>
              </CardContent>
            </Card>
          </section>

          <section className="mb-16">
            <div className="flex items-center gap-2 mb-8">
              <span className="text-2xl">✅</span>
              <h2 className="text-3xl font-bold text-foreground">Why Work With Us</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <Card key={index} className="text-center">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 justify-center">
                      <span className="text-primary text-lg">✓</span>
                      <span className="text-lg font-medium">{benefit}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-8">
              <span className="text-2xl">💼</span>
              <h2 className="text-3xl font-bold text-foreground">How to Apply</h2>
            </div>
            
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <p className="text-lg">
                    Send your resume and a short intro to:{" "}
                    <a href="mailto:hiring@tailorwiz.com" className="text-primary hover:underline font-semibold">
                      hiring@tailorwiz.com
                    </a>
                  </p>
                  <p className="text-muted-foreground">
                    Tell us why you're interested and include a link to your portfolio or GitHub (if relevant).
                  </p>
                  <Button size="lg" className="mt-4" asChild>
                    <a href="mailto:hiring@tailorwiz.com?subject=Application for TailorWiz Team">
                      Apply Now
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </main>
      </div>
    </AppLayout>
  );
}
